import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './adulto-idosos.css';

export const AI_COLORS = {
  blue400: '#29B6F6',
  blue800: '#0277BD',
  blue900: '#01579B',
  bgBlue50: '#E3F2FD',
  bgBlue100: '#B3E5FC',
  bgPurple50: '#EDE7F6',
  bgAmber50: '#FFF8E1',
  amber300: '#FFB74D',
  green50: '#E8F5E9',
  green200: '#A5D6A7',
  pink50: '#FCE4EC',
  teal50: '#E0F7FA',
  textDark: '#37474f',
  textMuted: '#546e7a',
  white: '#ffffff',
};

export const AI_FONTS = {
  title: "'DM Serif Display', serif",
  body: "'Open Sans', sans-serif",
};

/* Imagem simples (sem zoom/modal). */
export function ZoomImg({ src, alt, style }) {
  return <img src={src} alt={alt} style={style} />;
}

/*
 * Modal — janela sobreposta genérica (renderizada via portal no body).
 * Fecha ao clicar no fundo, no botão × ou ao pressionar Esc.
 */
export function Modal({ onClose, children, title, contentStyle }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(1, 26, 43, 0.72)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        animation: 'aiFadeIn 0.18s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          background: AI_COLORS.white,
          borderRadius: 14,
          maxWidth: 760,
          width: '100%',
          maxHeight: '85vh',
          overflowY: 'auto',
          padding: '28px 30px 30px',
          boxShadow: '0 16px 48px rgba(0,0,0,0.35)',
          ...contentStyle,
        }}
      >
        <button
          onClick={onClose}
          aria-label="Fechar"
          style={{
            position: 'sticky',
            float: 'right',
            top: 0,
            background: AI_COLORS.bgBlue50,
            color: AI_COLORS.blue900,
            border: 'none',
            borderRadius: '50%',
            width: 40,
            height: 40,
            fontSize: '1.5rem',
            lineHeight: 1,
            cursor: 'pointer',
          }}
        >
          ×
        </button>
        {title && (
          <h2
            style={{
              fontFamily: AI_FONTS.title,
              color: AI_COLORS.blue900,
              fontSize: '1.5rem',
              margin: '0 0 18px',
            }}
          >
            {title}
          </h2>
        )}
        {children}
      </div>
    </div>,
    document.body
  );
}

/*
 * LightboxImage — imagem que, ao ser clicada, abre em tela cheia (100vw x 100vh)
 * sobrepondo a tela como um modal.
 */
export function LightboxImage({ src, alt, caption, maxWidth = 520, style, referencia }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      <figure style={{ margin: '20px auto', textAlign: 'center', maxWidth, width: '100%' }}>
        <img
          src={src}
          alt={alt}
          onClick={() => setOpen(true)}
          title="Clique para ampliar"
          style={{
            display: 'block',
            width: '100%',
            height: 'auto',
            borderRadius: 8,
            cursor: 'zoom-in',
            ...style,
          }}
        />
        {caption && (
          <figcaption
            style={{
              fontSize: '0.82rem',
              color: AI_COLORS.textMuted,
              marginTop: 8,
              fontFamily: AI_FONTS.body,
              fontStyle: 'italic',
            }}
          >
            {caption}
          </figcaption>
        )}
        <ReferenciaLabel referencia={referencia} />
      </figure>

      {open &&
        createPortal(
          <div
            onClick={() => setOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              width: '100vw',
              height: '100vh',
              zIndex: 9999,
              background: 'rgba(1, 26, 43, 0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'zoom-out',
              animation: 'aiFadeIn 0.18s ease',
            }}
          >
            <button
              onClick={() => setOpen(false)}
              aria-label="Fechar"
              style={{
                position: 'absolute',
                top: 16,
                right: 20,
                background: 'rgba(255,255,255,0.15)',
                color: '#fff',
                border: 'none',
                borderRadius: '50%',
                width: 44,
                height: 44,
                fontSize: '1.6rem',
                lineHeight: 1,
                cursor: 'pointer',
              }}
            >
              ×
            </button>
            <img
              src={src}
              alt={alt}
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: '100vw',
                maxHeight: '100vh',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                cursor: 'default',
              }}
            />
          </div>,
          document.body
        )}
    </>
  );
}

/* Texto padrão de referência/fonte, exibido logo abaixo de imagens e vídeos.
   Quando nenhuma referência específica é informada, usa o padrão "Canva (2026)". */
export function ReferenciaLabel({ referencia = 'Canva (2026)' }) {
  return (
    <p
      style={{
        fontSize: '0.75rem',
        color: AI_COLORS.textMuted,
        marginTop: 6,
        marginBottom: 0,
        fontFamily: AI_FONTS.body,
      }}
    >
      Fonte: {referencia}
    </p>
  );
}

/* Uma figura/placeholder individual. `inRow` ajusta o layout para o modo lado a lado. */
function SlotFigure({
  folder,
  filename,
  alt,
  caption,
  maxWidth = 320,
  referencia,
}) {
  return (
    <figure
      style={{
        margin: '20px auto',
        textAlign: 'center',
        maxWidth,
        width: '100%',
      }}
    >
      <ZoomImg
        src={filename}
        alt={alt}
        style={{
          display: 'block',
          width: '100%',
          height: 'auto',
          borderRadius: 8,
        }}
      />

      {caption && (
        <figcaption
          style={{
            fontSize: '0.82rem',
            color: AI_COLORS.textMuted,
            marginTop: 8,
            fontFamily: AI_FONTS.body,
            fontStyle: 'italic',
          }}
        >
          {caption}
        </figcaption>
      )}
      <ReferenciaLabel referencia={referencia} />
    </figure>
  );
}

/*
 * ImgSlot — placeholder de imagem.
 * Uso simples: <ImgSlot folder="agravos" filename="carie.png" alt="..." caption="..." referencia="..." />
 * Duas imagens lado a lado (empilham no mobile via flex-wrap). `referencia` é exibida abaixo da
 * primeira foto e `referencia2` abaixo da segunda; se `referencia2` não for informada, a mesma
 * `referencia` é repetida abaixo de cada foto:
 *   <ImgSlot
 *     folder="agravos" filename="carie-inicio.png" alt="Início" caption="Início"
 *     second={{ folder: 'agravos', filename: 'carie-fim.png', alt: 'Avançada', caption: 'Avançada' }}
 *     referencia="Fonte 1" referencia2="Fonte 2"
 *   />
 */
export function ImgSlot({
  folder,
  filename,
  alt,
  caption,
  maxWidth = 520,
  second,
  referencia,
  referencia2,
}) {
  if (!second) {
    return (
      <SlotFigure
        folder={folder}
        filename={filename}
        alt={alt}
        caption={caption}
        maxWidth={maxWidth}
        referencia={referencia}
      />
    );
  }

  return (
    <figure
      style={{
        margin: '20px auto',
        textAlign: 'center',
        maxWidth,
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            window.innerWidth < 768
              ? '1fr'
              : '1fr 1fr',
          gap: 12,
        }}
      >
        <div>
          <ZoomImg
            src={filename}
            alt={alt}
            style={{
              width: '100%',
              borderRadius: 8,
              objectFit: 'contain',
            }}
          />
          <ReferenciaLabel referencia={referencia} />
        </div>

        <div>
          <ZoomImg
            src={second}
            alt={second.alt}
            style={{
              width: '100%',
              borderRadius: 8,
              objectFit: 'contain',
            }}
          />
          <ReferenciaLabel referencia={referencia2 || referencia} />
        </div>
      </div>

      {caption && (
        <figcaption
          style={{
            fontSize: '0.82rem',
            color: AI_COLORS.textMuted,
            marginTop: 8,
            fontFamily: AI_FONTS.body,
            fontStyle: 'italic',
          }}
        >
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

/*
 * Confetti — chuva de confetes sem dependências externas.
 * Renderize quando `show` for verdadeiro; ele se autolimpa.
 *   {acertou && <Confetti />}
 */
export function Confetti({ pieces = 120, duration = 3000 }) {
  const cores = ['#29B6F6', '#0277BD', '#FFB74D', '#4CAF50', '#EF5350', '#AB47BC', '#FFD54F'];
  const confetes = React.useMemo(
    () =>
      Array.from({ length: pieces }).map((_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 0.6,
        dur: 2.4 + Math.random() * 1.8,
        cor: cores[i % cores.length],
        size: 6 + Math.random() * 8,
        redondo: Math.random() > 0.6,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pieces]
  );

  const [visivel, setVisivel] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setVisivel(false), duration);
    return () => clearTimeout(t);
  }, [duration]);

  if (!visivel) return null;

  return (
    <div className="ai-confetti-layer" aria-hidden="true">
      {confetes.map((c) => (
        <span
          key={c.id}
          className="ai-confetti-piece"
          style={{
            left: `${c.left}%`,
            width: c.size,
            height: c.redondo ? c.size : c.size * 1.4,
            borderRadius: c.redondo ? '50%' : 2,
            background: c.cor,
            animationDuration: `${c.dur}s`,
            animationDelay: `${c.delay}s`,
          }}
        />
      ))}
    </div>
  );
}

export function TipBox({ icon = '💡', children }) {
  return (
    <div
      style={{
        background: AI_COLORS.bgAmber50,
        borderLeft: `4px solid ${AI_COLORS.amber300}`,
        borderRadius: 8,
        padding: '14px 18px',
        margin: '20px 0',
        display: 'flex',
        gap: 10,
        lineHeight: 1.7,
        fontFamily: AI_FONTS.body,
        fontSize: '0.95rem',
        color: AI_COLORS.textDark,
      }}
    >
      <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>{icon}</span>
      <div>{children}</div>
    </div>
  );
}

/* URL de um áudio servido a partir de public/audios */
export function audioUrl(name) {
  return `${process.env.PUBLIC_URL || ''}/audios/${name}`;
}

function fmt(t) {
  if (!t || Number.isNaN(t)) return '0:00';
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/* Player de áudio real — recebe o nome do arquivo (ex.: "carie.m4a") */
export function AudioPlayer({ src, label = 'Prefere ouvir? Clique para escutar um resumo rápido' }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);

  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (a.paused) {
      a.play();
      setPlaying(true);
    } else {
      a.pause();
      setPlaying(false);
    }
  };

  const onTime = () => {
    const a = ref.current;
    if (!a) return;
    setCur(a.currentTime);
    setProgress(a.duration ? (a.currentTime / a.duration) * 100 : 0);
  };

  const seek = (e) => {
    const a = ref.current;
    if (!a || !a.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    a.currentTime = ((e.clientX - rect.left) / rect.width) * a.duration;
  };

  return (
    <div className="ai-audio">
      <button
        className="ai-audio-btn"
        onClick={toggle}
        aria-label={playing ? 'Pausar áudio' : 'Reproduzir áudio'}
      >
        {playing ? '❚❚' : '▶'}
      </button>
      <div className="ai-audio-info">
        <span className="ai-audio-label">🎧 {label}</span>
        <div className="ai-audio-track" onClick={seek}>
          <div className="ai-audio-fill" style={{ width: `${progress}%` }} />
        </div>
        <span className="ai-audio-time">{fmt(cur)} / {fmt(dur)}</span>
      </div>
      <audio
        ref={ref}
        src={audioUrl(src)}
        preload="metadata"
        onTimeUpdate={onTime}
        onLoadedMetadata={() => setDur(ref.current?.duration || 0)}
        onEnded={() => { setPlaying(false); setProgress(0); setCur(0); }}
      />
    </div>
  );
}

/* Compatibilidade: aceita um nome de áudio opcional */
export function AudioBtn({ audio, label }) {
  if (!audio) return null;
  return <AudioPlayer src={audio} label={label} />;
}

/* Player de vídeo do Google Drive (responsivo 16:9).
   Use o ID do arquivo do Drive: <VideoDrive id="ABC123" title="..." /> */
export function VideoDrive({ id, title = 'Vídeo', maxWidth = 560, referencia }) {
  return (
    <div style={{ width: '100%', maxWidth, margin: '16px auto', textAlign: 'center' }}>
      <div
        style={{
          position: 'relative',
          paddingBottom: '56.25%',
          height: 0,
          borderRadius: 12,
          overflow: 'hidden',
          boxShadow: '0 6px 20px rgba(1, 87, 155, 0.15)',
        }}
      >
        <iframe
          title={title}
          src={`https://drive.google.com/file/d/${id}/preview`}
          allow="autoplay; encrypted-media"
          allowFullScreen
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
        />
      </div>
      <ReferenciaLabel referencia={referencia} />
    </div>
  );
}

/* Camada de fundo ambiente — blobs animados que reagem ao mouse */
export function AmbientBackground() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = null;
    const onMove = (e) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        const mx = (e.clientX / window.innerWidth - 0.5) * 2;
        const my = (e.clientY / window.innerHeight - 0.5) * 2;
        el.style.setProperty('--mx', mx.toFixed(3));
        el.style.setProperty('--my', my.toFixed(3));
        raf = null;
      });
    };
    window.addEventListener('mousemove', onMove);
    return () => {
      window.removeEventListener('mousemove', onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  return (
    <div className="ai-ambient" ref={ref} aria-hidden="true">
      <span className="ai-blob b1" />
      <span className="ai-blob b2" />
      <span className="ai-blob b3" />
    </div>
  );
}

/* Bolhas que sobem dentro do hero */
function HeroBubbles() {
  const bubbles = [
    { l: '8%', s: 26, d: 13, delay: 0 },
    { l: '22%', s: 16, d: 17, delay: 3 },
    { l: '40%', s: 34, d: 15, delay: 1 },
    { l: '58%', s: 20, d: 19, delay: 5 },
    { l: '74%', s: 28, d: 14, delay: 2 },
    { l: '88%', s: 18, d: 18, delay: 4 },
  ];
  return (
    <div className="ai-hero-bubbles" aria-hidden="true">
      {bubbles.map((b, i) => (
        <span
          key={i}
          className="ai-bubble"
          style={{
            left: b.l,
            width: b.s,
            height: b.s,
            animationDuration: `${b.d}s`,
            animationDelay: `${b.delay}s`,
          }}
        />
      ))}
    </div>
  );
}

/* Cartão com brilho que segue o cursor (fundo interativo) */
export function GlowCard({ children, style, onClick }) {
  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--gx', `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty('--gy', `${e.clientY - r.top}px`);
  };
  return (
    <div className="ai-glow-card" onMouseMove={move} onClick={onClick} style={style}>
      {children}
    </div>
  );
}

/* Revela o conteúdo com fade-up ao entrar na viewport */
export function FadeUp({ children, style }) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVis(true);
          obs.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={`ai-fade-up${vis ? ' is-visible' : ''}`} style={style}>
      {children}
    </div>
  );
}

export function CheckItem({ children }) {
  return (
    <li
      style={{
        display: 'flex',
        gap: 10,
        marginBottom: 10,
        alignItems: 'flex-start',
        fontFamily: AI_FONTS.body,
        lineHeight: 1.6,
        listStyle: 'none',
      }}
    >
      <span style={{ color: '#43A047', fontWeight: 'bold', flexShrink: 0 }}>✅</span>
      <span>{children}</span>
    </li>
  );
}

export function SectionTitle({ children, style }) {
  return (
    <h2
      style={{
        fontFamily: AI_FONTS.title,
        fontSize: '1.7rem',
        color: AI_COLORS.blue800,
        marginTop: 40,
        marginBottom: 16,
        lineHeight: 1.2,
        ...style,
      }}
    >
      {children}
    </h2>
  );
}

export function SubTitle({ children, style }) {
  return (
    <h3
      style={{
        fontFamily: AI_FONTS.title,
        fontSize: '1.25rem',
        color: AI_COLORS.blue900,
        marginTop: 24,
        marginBottom: 10,
        ...style,
      }}
    >
      {children}
    </h3>
  );
}

export function PageHero({ title, subtitle, audio }) {
  return (
    <div
      className="ai-hero"
      style={{
        background: 'linear-gradient(135deg, #E3F2FD 0%, #B3E5FC 55%, #EDE7F6 100%)',
        padding: '110px 24px 56px',
        textAlign: 'center',
      }}
    >
      <HeroBubbles />
      <div style={{ maxWidth: 760, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <h1
          style={{
            fontFamily: AI_FONTS.title,
            fontSize: 'clamp(1.9rem, 4.5vw, 2.8rem)',
            color: AI_COLORS.blue900,
            marginBottom: 14,
            lineHeight: 1.15,
          }}
        >
          {title}
        </h1>
        {subtitle && (
          <p
            style={{
              fontSize: '1.05rem',
              color: AI_COLORS.textDark,
              lineHeight: 1.75,
              fontFamily: AI_FONTS.body,
              margin: 0,
            }}
          >
            {subtitle}
          </p>
        )}
        {audio && (
          <div style={{ marginTop: 22 }}>
            <AudioPlayer src={audio} />
          </div>
        )}
      </div>
    </div>
  );
}

export function ContentWrap({ children }) {
  return (
    <div
      style={{
        position: 'relative',
        zIndex: 1,
        maxWidth: 940,
        margin: '0 auto',
        padding: '40px clamp(18px, 4vw, 40px) 120px',
        fontFamily: AI_FONTS.body,
        color: AI_COLORS.textDark,
        lineHeight: 1.7,
      }}
    >
      <div
        style={{
          background: 'rgba(255, 255, 255, 0.82)',
          backdropFilter: 'blur(4px)',
          borderRadius: 20,
          padding: 'clamp(20px, 4vw, 40px)',
          boxShadow: '0 8px 30px rgba(1, 87, 155, 0.08)',
        }}
      >
        {children}
      </div>
    </div>
  );
}

export function Card({ children, variant = 0, style }) {
  const bg = variant % 2 === 0 ? AI_COLORS.bgBlue50 : AI_COLORS.bgPurple50;
  return (
    <div
      style={{
        background: bg,
        borderRadius: 12,
        padding: '18px 22px',
        marginBottom: 14,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function StepCircle({ number }) {
  return (
    <div
      style={{
        background: AI_COLORS.blue400,
        color: 'white',
        borderRadius: '50%',
        width: 30,
        height: 30,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        flexShrink: 0,
        fontSize: '0.88rem',
        fontFamily: AI_FONTS.body,
      }}
    >
      {number}
    </div>
  );
}